import CheckSharpIcon from '@mui/icons-material/CheckSharp'
import ContentCopySharpIcon from '@mui/icons-material/ContentCopySharp'
import { IconButton, Tooltip } from '@mui/material'
import React, { useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { capitalizeFirstLetter, resolveNestedObject } from '../../../utils/helper'
import { useNotification } from '../../../utils/hooks'

interface CopyAddressButtonProps {
  prefix?: string
}

const CopyAddressButton: React.FC<CopyAddressButtonProps> = ({ prefix = '' }) => {
  const { t } = useTranslation('common')
  const { setNotification } = useNotification()
  const [copied, setCopied] = useState(false)

  const attributes = [
    'salutation',
    'company',
    'firstname',
    'lastname',
    'street',
    'houseNumber',
    'postalCode',
    'place',
    'country'
  ]
  const methods = useFormContext()
  const { getValues } = methods || {}

  const generateAddress = () => {
    const values = getValues()
    const obj: { [key: string]: string } = {}

    attributes.forEach(attribute => {
      const key = prefix.includes('.') ? attribute : capitalizeFirstLetter(attribute)

      obj[attribute] = resolveNestedObject(`${prefix}${key}`, values)
    })

    const {
      salutation,
      firstname,
      lastname,
      company,
      country,
      street,
      houseNumber,
      postalCode,
      place
    } = obj || {}
    const person = [firstname, lastname].filter(l => !!l).join(' ')
    const personLine = person ? [salutation, person].join(' ') : undefined
    const firstLines = [company, personLine].filter(l => !!l).join('\n')
    const countryLine = country ? `${country}\n` : undefined
    return [
      firstLines,
      `${street ?? ''} ${houseNumber ?? ''}`,
      `${postalCode ?? ''} ${place ?? ''}`,
      countryLine
    ]
      .filter(l => !!l)
      .join('\n')
  }

  const copyToClipboard = async () => {
    const address = generateAddress()
    try {
      await navigator.clipboard.writeText(address)
      setCopied(true)
      setNotification({ message: t('copyAddress.success'), open: true, variant: 'success' })
      setTimeout(() => {
        setCopied(false)
      }, 2000)
    } catch (error) {
      setNotification({ message: t('copyAddress.error'), open: true, variant: 'error' })
      console.error(t('copyAddress.error'), error)
    }
  }

  return (
    <Tooltip title={t('copyAddress.copy')} placement="top">
      <IconButton size="small" onClick={copyToClipboard}>
        {copied ? <CheckSharpIcon /> : <ContentCopySharpIcon />}
      </IconButton>
    </Tooltip>
  )
}

export default CopyAddressButton
